import { useDirectMarkerUpdates } from 'components/minute/builders/v2/useDirectMarkerUpdates';
import { useCreateWorldMarkerV2 } from 'hooks/marker/v2/useCreateWorldMarkerV2';
import { ICoordinates } from 'libs/types/map/mapTypes';
import { throttle } from 'lodash';
import { MapMouseEvent } from 'mapbox-gl';
import { useContext, useEffect } from 'react';
import { IDirectionWorldMarker, Transport } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { getTransportPath } from 'static/externalApi/directionApi';

export const DirectionBuilder = () => {
    const ctxEdit = useContext(CtxEdit);
    const mapRef = ctxEdit.mapRef.current;

    const marker = ctxEdit.markerCreation.marker as IDirectionWorldMarker | null;
    const start = marker?.elementData?.coordinates?.[0];

    const createWorldMarkerV2 = useCreateWorldMarkerV2();
    const directMarkerUpdates = useDirectMarkerUpdates();

    const getPath = throttle(
        async (transport: Transport, coordinates: ICoordinates[]) => {
            return await getTransportPath(transport, coordinates);
        },
        500,
        { leading: true, trailing: true }
    );

    const onMouseClick = async (e: MapMouseEvent) => {
        const newCoordinate = { lat: e.lngLat.lat, lng: e.lngLat.lng };
        if (!start) {
            createWorldMarkerV2.createDirection(newCoordinate);
            directMarkerUpdates.addTempCircle(newCoordinate, ctxEdit.markerDefault.color);
        } else {
            createWorldMarkerV2.finishDirection(newCoordinate);
            directMarkerUpdates.removeTempCirlce();
        }
    };

    const onMouseMove = async (e: MapMouseEvent) => {
        if (!start || !marker) {
            return;
        }
        const newCoordinate = { lat: e.lngLat.lat, lng: e.lngLat.lng };
        const newCoordinates = [start, newCoordinate];
        const { path, waypoints } = await getPath(marker.elementData.transport, newCoordinates);
        directMarkerUpdates.updateDirection(marker.id, waypoints, path);
    };

    const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            ctxEdit.markerCreation.remove();
            e.preventDefault();
            e.stopPropagation();
        }
    };

    useEffect(() => {
        if (mapRef) {
            mapRef.getCanvas().style.cursor = 'pointer';
            mapRef.on('click', onMouseClick);
            mapRef.on('mousemove', onMouseMove);
        }

        if (marker && !ctxEdit.markerCreation) {
            mapRef.getCanvas().style.cursor = 'default';
            mapRef?.off('click', onMouseClick);
            mapRef?.off('mousemove', onMouseMove);
        }

        return () => {
            mapRef.getCanvas().style.cursor = 'default';
            mapRef?.off('click', onMouseClick);
            mapRef?.off('mousemove', onMouseMove);
        };
    }, [mapRef, marker, start, ctxEdit.markerCreation.marker]);

    useEffect(() => {
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
        };
    });

    if (!ctxEdit.markerCreation.marker) {
        return null;
    }

    return <></>;
};
